/* eslint-disable max-lines */
import _ from 'underscore'
import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'

import WistiaVideoFields from 'pages/ResourceAdmin/ResourceFormModal/WistiaVideoFields'
import PdfFields from 'pages/ResourceAdmin/ResourceFormModal/PdfFields'
import ThermostatFields from 'pages/ResourceAdmin/ResourceFormModal/ThermostatFields'

import ModalButton from 'components/Modal/Button'
import Checkbox from 'components/Checkbox'
import ErrorMessage from 'components/ErrorMessage'
import ModalFormSection from 'pages/shared/ModalFormSection'
import Modal from 'components/Modal'
import RadioInputs from 'components/RadioInputs'
import TextInput from 'components/TextInput'
import Textarea from 'components/Textarea'

import Api from 'services/api'

import { ButtonContainer, screenSize } from 'styles/shared'

import Resource, { ResourceRequest, ValidThermostatModels, ValidResourceTypes, EditableResourceProps } from 'types/Resource'

type errors = {
  pdfFileOrWistiaVideoId?: string
  name?: string
  description?: string
  brand?: string
  version?: string
  models?: string
  releaseDate?: string
  saving?: string
}

type Props = {
  closeCallback: () => void
  isDisplayed: boolean,
  isEdit?: boolean,
  incomingType?: ValidResourceTypes,
  incomingResource?: Resource | null
  onSuccessfulSubmission: () => void
  editableResource:EditableResourceProps
  activeTab:'video'|'article'|'faq' | 'thermostat'
}

const ResourceFormModal = ({ closeCallback, isDisplayed, isEdit = false, onSuccessfulSubmission, editableResource, activeTab = 'video' }: Props) => {
  const { incomingType = 'video', incomingResource = null } = editableResource || {}
  const [isTraneDiagnostics, setIsTraneDiagnostics] = useState(false)
  const [isAmericanStandard, setIsAmericanStandard] = useState(false)
  const [type, setType] = useState<ValidResourceTypes>('video')
  const [wistiaVideoId, setWistiaVideoId] = useState('')
  const [pdfFile, setPdfFile] = useState(null)
  const [name, setName] = useState('')
  const [version, setVersion] = useState('')
  const [releaseDate, setReleaseDate] = useState('')
  const [models, setModel] = useState<Array<ValidThermostatModels>>([])
  const [description, setDescription] = useState('')
  const [draft, setDraft] = useState(true)
  const [errors, setErrors] = useState<errors>({})
  const id = incomingResource?.id || null
  const s3Key = incomingResource?.s3Key || null
  const [showSpinner, setShowSpinner] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const onSuccessfulWistiaSearch = (video) => {
    setWistiaVideoId(video.hashedId)
    setName(video.name)
    setDescription(video.description)
  }

  const onClickSave = () => {
    setShowSpinner(true)
    setErrors({})
    let newErrors = {}
    newErrors = validate(isEdit)
    if (!_.isEmpty(newErrors)) {
      setErrors(newErrors)
      setShowSpinner(false)
    } else {
      const brands: string[] = []
      if (isTraneDiagnostics) { brands.push('trane_home') }
      if (type !== 'thermostat' && isAmericanStandard) { brands.push('asair_home') }
      new Promise((resolve) => {
        (async () => {
          const failedBrands: string[] = []
          for (let i = 0; i < brands.length; i++) {
            const brand = brands[i]
            const requestPayload = { draft, brand, type, wistiaVideoId, pdfFile, name, version, models, description, releaseDate, id, s3Key } as ResourceRequest

            await Api.Resources.update(requestPayload)
              .catch(() => {
                failedBrands.push(brand)
              })
          }
          resolve(failedBrands)
        })()
      }).then((failedBrands: string[]) => {
        if (failedBrands.length) {
          setErrors({ saving: `Failed to save for ${failedBrands.join('and ')} brand${failedBrands.length > 1 ? 's' : ''}` })
          setShowSpinner(false)
        } else {
          onSuccessfulSubmission()
          closeCallback()
          setShowSpinner(false)
        }
      })
    }
  }

  const validate = (isEdit) => {
    const newErrors: errors = {}
    if (!isEdit) {
      if (type === 'video' && !wistiaVideoId) { newErrors.pdfFileOrWistiaVideoId = 'A valid Wistia video ID is required' }
      if ((type === 'article' && !pdfFile)) { newErrors.pdfFileOrWistiaVideoId = 'A PDF file is required' }
      if ((type === 'faq' && !pdfFile)) { newErrors.pdfFileOrWistiaVideoId = 'A PDF file is required' }
    }
    if (type !== 'thermostat' && !name) { newErrors.name = 'Name is required' }
    if (!description) { newErrors.description = 'Description is required' }
    if (type === 'thermostat' && !version) { newErrors.version = 'Version is required' }
    if (type === 'thermostat' && version && !/^\d+(\.\d+)*$/.test(version)) { newErrors.version = 'A valid version is required' }
    if (type === 'thermostat' && models.length === 0) { newErrors.models = 'At least one model is required' }
    if (type === 'thermostat' && (!releaseDate || releaseDate === 'Invalid date')) { newErrors.releaseDate = 'Release date is required' }
    if (type !== 'thermostat' && !isTraneDiagnostics && !isAmericanStandard) { newErrors.brand = 'At least one brand is required' }
    return newErrors
  }

  const onClickCancel = () => {
    setIsAmericanStandard(false)
    setIsTraneDiagnostics(false)
    setType('video')
    setName('')
    setDescription('')
    setModel([])
    setReleaseDate('')
    setDraft(true)
    setErrors({})
    closeCallback()
  }
  const onHandleResourceUpdate = useCallback(() => {
    if (incomingType) { setType(incomingType) }
    if (incomingResource && incomingType !== 'thermostat') {
      setWistiaVideoId(incomingResource.hashedId || '')
      setDraft(incomingResource.draft)
      setName(incomingResource.name)
      setDescription(incomingResource.description)
    } else if (incomingResource && incomingType === 'thermostat') {
      setDraft(incomingResource.draft)
      setVersion(incomingResource?.version || '')
      setDescription(incomingResource.description)
      setModel(incomingResource?.models || [])
      setReleaseDate(incomingResource?.releaseDate || '')
      setIsTraneDiagnostics(true)
    }
    if (incomingType !== 'thermostat' && incomingResource?.brand.includes('trane')) { setIsTraneDiagnostics(true) }
    if (incomingType !== 'thermostat' && incomingResource?.brand.includes('asair')) { setIsAmericanStandard(true) }
  }, [incomingType, incomingResource])

  const onClickDelete = () => {
    setIsDeleting(true)
    setShowSpinner(true)
    if (!id || !activeTab) {
      setShowSpinner(false)
      setIsDeleting(false)
      return
    }
    if (confirm('Are you sure you want to delete this resource?')) {
      Api.Resources.delete(id, incomingType ?? activeTab)
        .then(() => {
          onSuccessfulSubmission()
          closeCallback()
          setShowSpinner(false)
          setIsDeleting(false)
        })
    }
  }

  useEffect(() => {
    if (incomingType === 'thermostat' || type === 'thermostat') {
      setIsTraneDiagnostics(true)
    }
  }, [incomingResource, incomingType, type])
  useEffect(onHandleResourceUpdate, [onHandleResourceUpdate, isDisplayed])

  return (
    <Modal title={isEdit ? 'Edit Resource' : 'Add Resource'} variant="sm" {...{ closeCallback, isDisplayed }}>
      <Checkbox
        label="Draft"
        checked={draft}
        onChange={() => setDraft(!draft)}
      />
      {type !== 'thermostat' &&
        <ModalFormSection title="Brand">
          <Checkbox
            label="Trane Diagnostics"
            checked={isTraneDiagnostics}
            disabled={isEdit}
            onChange={() => setIsTraneDiagnostics(!isTraneDiagnostics)}
            errorMessage={errors.brand}
          />
          <Checkbox
            label="American Standard"
            checked={isAmericanStandard}
            disabled={isEdit}
            onChange={() => setIsAmericanStandard(!isAmericanStandard)}
          />
        </ModalFormSection>
      }
      {!isEdit && (
        <ModalFormSection title="Type">
          <RadioInputs
            selectedValue={type}
            onChange={(event) => setType(event.target.value)}
            radioOptions={[
              { label: 'Video', value: 'video' },
              { label: 'Article', value: 'article' },
              { label: 'FAQs', value: 'faq' },
              { label: 'Thermostat Releases', value: 'thermostat' }
            ]}
          />
          {errors.pdfFileOrWistiaVideoId && <S.ErrorMessage errorMessage={errors.pdfFileOrWistiaVideoId} />}
        </ModalFormSection>
      )}
      <S.SpecializedFieldsSection>
        {type === 'video' && <WistiaVideoFields onSuccessfulSearch={onSuccessfulWistiaSearch} loadedVideoName={incomingResource?.name} />}
        {type === 'article' && <PdfFields onChangePdf={(pdfFile) => { setPdfFile(pdfFile) }} loadedPdfName={incomingResource?.s3Key} />}
        {type === 'faq' && <PdfFields onChangePdf={(pdfFile) => { setPdfFile(pdfFile) }} loadedPdfName={incomingResource?.s3Key} />}
      </S.SpecializedFieldsSection>
      {type !== 'thermostat'
        ? <TextInput
            isRequired
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            errorMessage={errors.name}
            disabled={type === 'video'}
          />
        : <ThermostatFields
            version={version}
            models={models}
            releaseDate={releaseDate}
            setVersion={setVersion}
            setModel={setModel}
            setReleaseDate={setReleaseDate}
            errors={errors}
          />
      }
      <Textarea
        isRequired
        label="Description"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        errorMessage={errors.description}
        disabled={type === 'video'}
      />

      {errors.saving && <S.SavingErrorMessage errorMessage={errors.saving} />}
      <ButtonContainer variant={isEdit ? 'submit-delete' : 'submit'} noBorder>
        {isEdit && <ModalButton onPress={onClickDelete} decoration="danger" text="Delete Resource" showSpinner={showSpinner && isDeleting} />}

        <S.RightSide>
          <ModalButton onPress={onClickCancel} decoration="secondary" text="Cancel" />
          <ModalButton onPress={onClickSave} decoration="positive" text="Save" showSpinner={showSpinner && !isDeleting} />
        </S.RightSide>
      </ButtonContainer>
    </Modal>
  )
}

export default ResourceFormModal

const S = {
  ErrorMessage: styled(ErrorMessage)`
    margin-top: -20px;
   
    @media (${screenSize.small}) {
      margin-top: -55px;
    }
  `,
  SavingErrorMessage: styled(ErrorMessage)`
    margin-top: -10px;
   
    @media (${screenSize.small}) {
      margin-top: -55px;
    }
  `,
  SpecializedFieldsSection: styled.div`
    padding: 10px 0;
  `,
  RightSide: styled.div`
    display: flex
  `
}
